import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import PauseCircleOutlineIcon from 'mdi-react/PauseCircleOutlineIcon';
import PlayCircleOutlineIcon from 'mdi-react/PlayCircleOutlineIcon';
import React, { useCallback, useEffect } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import {
  useDisableUpdateMutation,
  useEnableUpdateMutation,
  useGenerateDeeplinkMutation,
  useGetUpdateQuery,
} from 'store/app/entities/OtaSlice';
import { I18n } from 'utils/i18n';

function formatApiError(error) {
  const err = error?.data?.error;
  if (typeof err === 'string') return err;
  if (err && typeof err.message === 'string') return err.message;
  if (typeof error?.message === 'string') return error.message;
  return I18n.t('AdminOtaScene.loadError');
}

const copyText = async text => {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    window.prompt('Copy', text);
  }
};

const CopyIconButton = ({ value, label }) => (
  <Tooltip title={label}>
    <IconButton
      size="small"
      onClick={() => copyText(value)}
      aria-label={label}
      sx={{ ml: 0.5, color: 'text.secondary' }}
    >
      <SvgIcon sx={{ fontSize: 16 }}>
        <ContentCopyIcon />
      </SvgIcon>
    </IconButton>
  </Tooltip>
);

CopyIconButton.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

const CopyableLine = ({ children, value = '', copyLabel, mono = false, variant = 'body2' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
    <Typography
      variant={variant}
      sx={{
        color: variant === 'body2' ? 'text.secondary' : 'text.primary',
        fontFamily: mono ? 'monospace' : 'inherit',
        wordBreak: mono ? 'break-all' : 'normal',
        minWidth: 0,
      }}
    >
      {children}
    </Typography>
    {value ? <CopyIconButton value={value} label={copyLabel} /> : null}
  </Box>
);

CopyableLine.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.string,
  copyLabel: PropTypes.string.isRequired,
  mono: PropTypes.bool,
  variant: PropTypes.string,
};

const InfoRow = ({ label, value, copyValue = '', copyLabel = '' }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
    {copyValue ? (
      <CopyableLine value={copyValue} copyLabel={copyLabel} mono variant="body2">
        <Box component="span" sx={{ color: 'text.primary' }}>
          {value}
        </Box>
      </CopyableLine>
    ) : (
      <Typography variant="body2">{value}</Typography>
    )}
  </Box>
);

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  copyValue: PropTypes.string,
  copyLabel: PropTypes.string,
};

const formatDate = date => {
  if (!date) return '—';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
};

const OtaUpdateDetailPage = () => {
  const { id } = useParams();
  const { data, isLoading, error } = useGetUpdateQuery(id, { skip: !id });
  const [disableUpdate] = useDisableUpdateMutation();
  const [enableUpdate] = useEnableUpdateMutation();
  const [
    generateDeeplink,
    { data: deeplinkData, isLoading: deeplinkLoading, error: deeplinkError },
  ] = useGenerateDeeplinkMutation();

  const u = data?.update;
  const deeplinkUrl = deeplinkData?.url || '';

  const loadDeeplink = useCallback(() => {
    if (id) generateDeeplink(id);
  }, [id, generateDeeplink]);

  useEffect(() => {
    loadDeeplink();
  }, [loadDeeplink]);

  if (isLoading) return null;
  if (error || !u) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          {formatApiError(error) || I18n.t('AdminOtaScene.UpdateDetail.notFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button
          component={RouterLink}
          to="/home/admin/ota/updates"
          variant="text"
          color="secondary"
          size="small"
          sx={{ ml: -1 }}
        >
          <SvgIcon sx={{ fontSize: 18, mr: 0.5 }}>
            <ArrowLeftIcon />
          </SvgIcon>
          {I18n.t('AdminOtaScene.nav.backUpdates')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 4 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h3">
              {u.revision != null ? `rev.${u.revision}` : String(u._id).slice(-8)}
            </Typography>
            <Chip
              size="small"
              label={I18n.t(
                u.enabled
                  ? 'AdminOtaScene.Updates.statusEnabled'
                  : 'AdminOtaScene.Updates.statusDisabled',
              )}
              variant="soft"
              color={u.enabled ? 'success' : 'default'}
              sx={{ alignSelf: 'flex-start' }}
            />
          </Box>
          <Box sx={{ mt: 0.5 }}>
            <CopyableLine
              value={u.manifestId}
              copyLabel={I18n.t('AdminOtaScene.UpdateDetail.copyManifestId')}
              mono
            >
              {u.manifestId}
            </CopyableLine>
          </Box>
        </Box>
        {u.enabled ? (
          <Button variant="outlined" color="warning" onClick={() => disableUpdate(String(u._id))}>
            <SvgIcon sx={{ fontSize: 18, mr: 0.5 }}>
              <PauseCircleOutlineIcon />
            </SvgIcon>
            {I18n.t('AdminOtaScene.Updates.disable')}
          </Button>
        ) : (
          <Button variant="outlined" color="primary" onClick={() => enableUpdate(String(u._id))}>
            <SvgIcon sx={{ fontSize: 18, mr: 0.5 }}>
              <PlayCircleOutlineIcon />
            </SvgIcon>
            {I18n.t('AdminOtaScene.Updates.enable')}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 3,
          rowGap: 2,
          p: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 4,
        }}
      >
        <InfoRow label={I18n.t('AdminOtaScene.Updates.channel')} value={u.channelName || '—'} />
        <InfoRow label={I18n.t('AdminOtaScene.Updates.colBranch')} value={u.branch || '—'} />
        <InfoRow label={I18n.t('AdminOtaScene.Updates.colPlatform')} value={u.platform || '—'} />
        <InfoRow
          label={I18n.t('AdminOtaScene.Updates.colRuntime')}
          value={u.runtimeVersion || '—'}
          copyValue={u.runtimeVersion || ''}
          copyLabel={I18n.t('AdminOtaScene.UpdateDetail.copyRuntime')}
        />
        <InfoRow
          label={I18n.t('AdminOtaScene.UpdateDetail.updateId')}
          value={String(u._id)}
          copyValue={String(u._id)}
          copyLabel={I18n.t('AdminOtaScene.UpdateDetail.copyUpdateId')}
        />
        <InfoRow
          label={I18n.t('AdminOtaScene.UpdateDetail.appVersion')}
          value={u.appVersion || '—'}
        />
        <InfoRow
          label={I18n.t('AdminOtaScene.Updates.colCreated')}
          value={formatDate(u.createdAt)}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="h5">{I18n.t('AdminOtaScene.UpdateDetail.qaSection')}</Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mt: 0.5,
          }}
        >
          {I18n.t('AdminOtaScene.UpdateDetail.qaDescription')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'center', sm: 'flex-start' },
          gap: 3,
          p: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {deeplinkLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={22} />
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {I18n.t('AdminOtaScene.UpdateDetail.deeplinkLoading')}
            </Typography>
          </Box>
        )}
        {!deeplinkLoading && deeplinkError && (
          <Box>
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {formatApiError(deeplinkError)}
            </Typography>
            <Button size="small" variant="outlined" onClick={loadDeeplink}>
              {I18n.t('AdminOtaScene.UpdateDetail.retryDeeplink')}
            </Button>
          </Box>
        )}
        {!deeplinkLoading && !deeplinkError && deeplinkUrl && (
          <>
            <Box
              sx={{
                flex: '0 0 auto',
                p: 1.5,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <QRCodeSVG value={deeplinkUrl} size={160} level="M" />
            </Box>
            <Box sx={{ minWidth: 0, alignSelf: 'center' }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              >
                {I18n.t('AdminOtaScene.UpdateDetail.qaLink')}
              </Typography>
              <CopyableLine
                value={deeplinkUrl}
                copyLabel={I18n.t('AdminOtaScene.UpdateDetail.copyDeeplink')}
                mono
              >
                {deeplinkUrl}
              </CopyableLine>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default OtaUpdateDetailPage;
